import { metrics } from '@seek/metrics-js';
import { useEffect } from 'react';
import { v4 as uuid } from 'uuid';

import {
  type ActionOrigin,
  type Page,
  type PageSection,
  UpdateType,
  useTrackingClient,
} from 'src/context/tracking';
import { useJobId } from 'src/context/useJobId';
import { useEnhancedHubble } from 'src/hooks/useEnhancedHubble';
import { useGetVerifications } from 'src/hooks/useGetVerifications';
import { usePriorityApplyCredit } from 'src/hooks/usePriorityApplyCredit';
import { tagRecording } from 'src/modules/hotjar';
import { useSetHubblePage } from 'src/modules/hubble';
import { getJobSolMetadata } from 'src/modules/solTracker/solMetadata';

const trackingCorrelationId = uuid();

export function useTracking({
  isCandidateNewToWorkforce,
  currentPage,
  pageSection,
}: {
  isCandidateNewToWorkforce: boolean;
  currentPage: Page;
  pageSection: PageSection;
}) {
  const { jobId } = useJobId();
  const hubble = useEnhancedHubble();
  useSetHubblePage(currentPage);
  const client = useTrackingClient();
  const solMetadata = getJobSolMetadata(jobId);
  const { hasDigitalIdentityNudge, loading: verificationsLoading } =
    useGetVerifications();
  const {
    priorityApplyCredit,
    priorityApplyEnabled,
    priorityApplyCreditEnabled,
    loading: creditLoading,
  } = usePriorityApplyCredit();
  const isUnverified = hasDigitalIdentityNudge && priorityApplyEnabled;
  const remainingCount = priorityApplyCredit?.credit2?.remainingCount || 0;
  const isVerified =
    !hasDigitalIdentityNudge &&
    priorityApplyCreditEnabled &&
    remainingCount > 0;
  const isPriorityApplyNudgeShown =
    (isUnverified || isVerified) && remainingCount > 0;

  const sharedProps = {
    currentPage,
    pageTitle: currentPage,
    isExitLink: false,
    isCandidateNewToWorkforce,
    trackingCorrelationId,
  };

  useEffect(() => {
    if (!verificationsLoading && !creditLoading) {
      client.trackView({
        eventName: 'apply_review_displayed',
        view: currentPage,
        currentPageSection: pageSection,
        ...{
          isPriorityApplyNudgeShown,
        },
      });

      metrics.count('APPLY_REVIEW_DISPLAYED');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationsLoading, creditLoading]);

  const trackSubmitSucceeded = ({
    profileVisibilityLevel,
    previousProfileVisibility,
    currentPageSection,
    currentPageSubsection,
    actionOrigin,
    applicationId,
    applicationCorrelationId,
    isResumeAttached,
    isCoverLetterAttached,
    resumeHasSensitivePersonalInfo,
    isStrongInterest,
  }: {
    profileVisibilityLevel: string;
    previousProfileVisibility: string;
    currentPageSection: PageSection;
    currentPageSubsection: string;
    actionOrigin: ActionOrigin;
    applicationId: string;
    applicationCorrelationId: string;
    isResumeAttached: boolean;
    isCoverLetterAttached: boolean;
    resumeHasSensitivePersonalInfo: boolean;
    isStrongInterest: boolean;
  }) => {
    hubble.trackEvent(
      'job_apply_submit_succeeded',
      {
        jobId,
        thirdPartyLink: false,
        solMetadata,
        applicationId,
        applicationCorrelationId,
      },
      { trackingCorrelationId },
    );

    // If the user's profile visibility level has been changed, need to fire a seperate event to track the profile update
    if (profileVisibilityLevel !== previousProfileVisibility) {
      client.trackLink({
        ...sharedProps,
        eventName: 'profile_update_succeeded',
        link: 'profile_update_succeeded',
        actionOrigin,
        currentPageSection,
        currentPageSubsection,
        profileVisibilityLevel,
        previousProfileVisibility,
        isResumeAttached,
        isCoverLetterAttached,
        resumeHasSensitivePersonalInfo,
        isStrongInterest,
        updateType: UpdateType.EDIT,
      });
    }

    client.trackLink({
      ...sharedProps,
      eventName: 'apply_submit_succeeded',
      link: 'apply_submit_succeeded',
      profileVisibilityLevel,
      isResumeAttached,
      isCoverLetterAttached,
      resumeHasSensitivePersonalInfo,
      isStrongInterest,
    });

    metrics.count('SUBMIT_APPLICATION_SUCCESS');
    tagRecording('application-submit-success');
  };

  const trackSubmitClick = ({
    profileVisibilityLevel,
  }: {
    profileVisibilityLevel: string;
  }) => {
    hubble.trackEvent('job_apply_submit_pressed', { jobId, solMetadata });

    client.trackLink({
      ...sharedProps,
      eventName: 'application_submit_pressed',
      link: 'application_submit_pressed',
      profileVisibilityLevel,
    });
  };

  return { trackSubmitSucceeded, trackSubmitClick };
}
